import { azureVariablesProblem } from './azure-config.js';
/**
 * What the start path and the preflight refuse of an Azure run before anything
 * is recorded (IM-46). A refusal here is a 400 that names the problem; the
 * same fault reaching the installer is a failed run in someone else's
 * subscription, or worse, one that succeeds somewhere else.
 */
/** An Azure location as ARM names it: `eastus2`, `westeurope`. */
const AZURE_LOCATION_SHAPE = /^[a-z][a-z0-9]{1,39}$/;
/**
 * Whether a string is shaped like an Azure location. A shape, not a list, for
 * `regionProblem`'s reasons: a list goes stale in the direction that refuses a
 * real location, and the account's own set-up is the real guard.
 */
export const azureLocationProblem = (location) => {
    if (location === '')
        return 'A location is required.';
    if (!AZURE_LOCATION_SHAPE.test(location)) {
        return `"${location}" is not shaped like an Azure location. Expected something like eastus2.`;
    }
    return undefined;
};
/**
 * Why an Azure run's region cannot be used, or undefined.
 *
 * - **A region, always.** The installer reads its region from
 *   `variables.location` and defaults to East US without an error (installer
 *   contract), which is IM-91's wrong-region failure one layer down. On AWS
 *   the control plane's own region is a usable default; on Azure it is not a
 *   location at all, so a new instance with none chosen is refused.
 * - **Shaped like an Azure location.**
 * - **`location` is the region, never a second opinion.** It is set from the
 *   run's region; a variable naming another one would deploy where the record
 *   does not say. The same value, which a refilled form sends, is accepted.
 *
 * Apart from `azureVariablesProblem` so the preflight can report it as a
 * region problem, with a region's fix, rather than as a variable to remove.
 */
export const azureRegionProblem = (input) => {
    if (input.region === undefined) {
        return 'An Azure run needs a region: choose the location for a new instance. The installer would otherwise deploy to East US without saying so.';
    }
    const shape = azureLocationProblem(input.region);
    if (shape !== undefined)
        return shape;
    const location = input.variables['location'];
    if (location !== undefined && location !== input.region) {
        return `variables.location (${JSON.stringify(location)}) is not this run's region (${input.region}). Choose the region beside the variables; location is set from it.`;
    }
    return undefined;
};
/**
 * Why an Azure run cannot start, or undefined: the variables IM-47 refuses,
 * by name (`azureVariablesProblem`), then the region (`azureRegionProblem`).
 * The start path's one call.
 */
export const azureRunProblem = (input) => azureVariablesProblem(input.variables) ?? azureRegionProblem(input);
//# sourceMappingURL=azure-run.js.map